import { useState } from 'react';
import { superAiApi } from '../api';
import type { ContextualPromptResult } from '../types';

interface Props {
  incomingText: string;
  threadId?: string;
  onInsert?: (text: string) => void;
  onOpenWorkspace?: (initialPrompt: string, contextMode: string) => void;
}

const CONTEXTUAL_ACTIONS = [
  { id: 'reply', label: 'Ответить' },
  { id: 'summarize', label: 'Резюме' },
  { id: 'draft_task', label: 'Создать задачу' },
  { id: 'escalate', label: 'Эскалировать' },
];

export const ContextualAiBubble = ({ incomingText, threadId, onInsert, onOpenWorkspace }: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [result, setResult] = useState<ContextualPromptResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [activeAction, setActiveAction] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleAction = async (action: string) => {
    if (isLoading) return;
    setActiveAction(action);
    setIsLoading(true);
    setError(null);
    setResult(null);
    try {
      const res = await superAiApi.contextualPrompt(action, incomingText, note || undefined, threadId);
      setResult(res);
    } catch {
      setError('AI временно недоступен. Попробуйте позже.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInsert = () => {
    if (result && onInsert) {
      onInsert(result.suggested_text);
      setIsOpen(false);
      setResult(null);
    }
  };

  const handleOpenWorkspace = () => {
    if (result && onOpenWorkspace) {
      onOpenWorkspace(result.suggested_text, result.action_type);
      setIsOpen(false);
    }
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-black border border-cyan-500/40 bg-cyan-500/10 text-cyan-400 hover:bg-cyan-500/20 hover:border-cyan-400/60 transition select-none"
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        id="contextual-ai-bubble-trigger"
      >
        ✦ AI
      </button>

      {isOpen && (
        <div
          className="absolute bottom-full mb-2 left-0 z-40 w-80 rounded-2xl border border-slate-700/80 bg-[#0b1928] shadow-2xl shadow-black/60 overflow-hidden"
          role="dialog"
          aria-label="Контекстный AI"
        >
          <div className="px-4 py-3 border-b border-slate-800 flex items-center justify-between">
            <span className="text-xs font-black text-slate-300">✦ Контекстный AI</span>
            <button type="button" onClick={() => setIsOpen(false)} className="text-slate-500 hover:text-slate-300 text-xs">✕</button>
          </div>

          <div className="px-3 py-2.5 bg-slate-950/40 border-b border-slate-800">
            <p className="text-[11px] text-slate-500 mb-1.5 font-semibold uppercase tracking-wider">Контекст</p>
            <p className="text-xs text-slate-400 line-clamp-3 leading-relaxed">{incomingText}</p>
          </div>

          <div className="px-3 pt-3 pb-2">
            <p className="text-[10px] text-slate-600 uppercase tracking-widest mb-2 font-bold">Действия</p>
            <div className="flex flex-wrap gap-1.5">
              {CONTEXTUAL_ACTIONS.map((action) => (
                <button
                  key={action.id}
                  type="button"
                  disabled={isLoading}
                  onClick={() => void handleAction(action.id)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition ${
                    activeAction === action.id && (isLoading || result)
                      ? 'border-cyan-500/60 bg-cyan-500/20 text-cyan-300'
                      : 'border-slate-700 text-slate-400 hover:text-slate-200 hover:bg-slate-800 hover:border-slate-600'
                  }`}
                >
                  {action.label}
                </button>
              ))}
            </div>
          </div>

          <div className="px-3 pb-3">
            <textarea
              rows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Дополнительный контекст (необязательно)..."
              className="w-full mt-1 bg-slate-900 border border-slate-800 rounded-lg px-3 py-2 text-xs text-slate-300 placeholder-slate-600 focus:outline-none focus:border-cyan-500 resize-none transition"
            />
          </div>

          {isLoading && (
            <div className="px-4 py-3 border-t border-slate-800 text-xs text-slate-500 animate-pulse flex items-center gap-2">
              <span className="inline-block w-2 h-2 rounded-full bg-cyan-500 animate-bounce" />
              AI обрабатывает...
            </div>
          )}

          {error && (
            <div className="px-4 py-3 border-t border-rose-500/30 bg-rose-500/10 text-xs text-rose-400">
              {error}
            </div>
          )}

          {result && !isLoading && (
            <div className="border-t border-slate-800">
              <div className="px-4 py-3 bg-slate-900/60">
                <p className="text-[10px] font-black uppercase tracking-widest text-cyan-400 mb-1.5">
                  Preview
                  {result.requires_approval && (
                    <span className="ml-2 text-amber-400">· требует подтверждения</span>
                  )}
                </p>
                <p className="text-xs text-slate-200 leading-relaxed whitespace-pre-line">{result.suggested_text}</p>
              </div>
              <div className="flex gap-2 px-4 py-3 border-t border-slate-800">
                {onInsert && !result.requires_approval && (
                  <button
                    type="button"
                    onClick={handleInsert}
                    className="flex-1 py-2 rounded-xl bg-cyan-500 hover:bg-cyan-400 text-slate-950 text-xs font-black transition"
                  >
                    Вставить
                  </button>
                )}
                {onOpenWorkspace && (
                  <button
                    type="button"
                    onClick={handleOpenWorkspace}
                    className="flex-1 py-2 rounded-xl border border-slate-600 hover:border-slate-500 bg-slate-900 hover:bg-slate-800 text-slate-300 text-xs font-semibold transition"
                  >
                    Открыть ✦ AI
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
